import type { BotDifficulty, Color, PlayerStatus } from "@ludo/engine";

const configuredApiUrl = import.meta.env.VITE_API_URL?.trim() ?? "";

function resolveApiBaseUrl() {
  if (configuredApiUrl) return configuredApiUrl.replace(/\/$/, "");
  if (typeof window === "undefined") return "";
  return window.location.origin;
}

const apiBaseUrl = resolveApiBaseUrl();

function buildUrl(path: string) {
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
  if (!apiBaseUrl) return normalizedPath ? `/${normalizedPath}` : "/";
  try {
    return new URL(normalizedPath, apiBaseUrl.endsWith("/") ? apiBaseUrl : `${apiBaseUrl}/`).toString();
  } catch {
    return `${apiBaseUrl.replace(/\/$/, "")}/${normalizedPath}`;
  }
}

export interface Session {
  token: string;
  user: { id: string; username: string; rating: number };
}

export interface RoomReservation {
  room: { roomId: string; processId: string; name: string };
  sessionId: string;
}

export interface PrivateRoomSeat {
  seat: Color;
  status: Extract<PlayerStatus, "human" | "bot" | "empty">;
  displayName: string;
  bot?: BotDifficulty;
}

export interface PrivateRoomConfig {
  hostSeat: Color;
  seats: PrivateRoomSeat[];
}

export interface TournamentConfig {
  title: string;
  hostSeat: Color;
  seats: PrivateRoomSeat[];
}

async function request<T>(path: string, init: RequestInit = {}, token?: string): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(buildUrl(path), {
    ...init,
    headers,
  });

  const text = await response.text();
  const body: unknown = text ? JSON.parse(text) : {};
  if (!response.ok) {
    throw new Error(typeof body === "object" && body !== null && "error" in body ? String(body.error) : "request_failed");
  }
  return body as T;
}

export const authApi = {
  register: (username: string, email: string, password: string) => request<Session>("/auth/register", { method: "POST", body: JSON.stringify({ username, email, password }) }),
  login: (email: string, password: string) => request<Session>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
};

export const roomsApi = {
  createPrivate: (token: string, config: PrivateRoomConfig) => request<{ roomCode: string; invitePath: string; reservation: RoomReservation }>('/rooms/private', { method: 'POST', body: JSON.stringify(config) }, token),
  joinPrivate: (token: string, roomCode: string, seat?: Color) => request<{ reservation: RoomReservation }>(`/rooms/private/${encodeURIComponent(roomCode)}/join`, { method: 'POST', body: JSON.stringify(seat === undefined ? {} : { seat }) }, token),
  quickMatch: (token: string, ranked = false) => request<{ status: 'queued' | 'matched'; reservation?: RoomReservation }>('/rooms/quick-match', { method: 'POST', body: JSON.stringify({ ranked }) }, token),
};

export const tournamentApi = {
  create: (token: string, config: TournamentConfig) => request<{ tournamentId: string; tournamentCode: string; invitePath: string; reservation: RoomReservation }>('/tournaments', { method: 'POST', body: JSON.stringify(config) }, token),
  join: (token: string, tournamentCode: string, seat?: Color) => request<{ tournamentId: string; reservation: RoomReservation }>(`/tournaments/${encodeURIComponent(tournamentCode)}/join`, { method: 'POST', body: JSON.stringify(seat === undefined ? {} : { seat }) }, token),
};
